import React, { ReactElement } from 'react';

import AppBar from '@mui/material/AppBar';
import ToolBar from '@mui/material/Toolbar';
import IconButton from "@mui/material/IconButton/IconButton";
import Badge from "@mui/material/Badge/Badge";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import { withStyles, WithStyles } from "@mui/styles";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import Menu from "@mui/material/Menu/Menu";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import {connect} from "react-redux";
import {bindActionCreators, compose, Dispatch} from "redux";
import {fetchWorkhoursCurrentWeek, fetchMenuItems} from "../../actions/menu/MainMenuActions";
import {fetchUnreadNotificationsCount, fetchUserNotificationUnread, markUserNotificationAsRead} from "../../actions/user/NotificationActions";
import { eventNotificationShow } from "../../actions/common/EventNotificationActions";
import NotificationDrawer from "./NotificationDrawer";
import { Theme, createStyles } from '@mui/material';
import { AppState } from '../../reducers/RootReducer';
import { MainMenuState } from '../../reducers/menu/MainMenuReducer';
import { UserNotificationState } from '../../reducers/user/NotificationReducer';
import { Nullable } from '../../models/common/Nullable';
import { LanguageProp} from '../../config/LanguageConfig';
import { userLogout } from '../../actions/common/AuthenticatedUserActions';
import HelpView from "./HelpView";
import {helpChangeViewableState} from "../../actions/common/HelpActions";
import {createEmployeeHoursReportKey, TimesheetReportState} from "../../reducers/reporting/TimesheetHoursReportReducer";
import {fetchMenuTimesheetHoursReport} from "../../actions/reporting/TimesheetReportActions";
import {getWeekRange} from "../../helpers/DateTimeHelpers";
import {DateRange} from "../../models/common/DateRange";
import {TimesheetEmployeeHoursReport} from "../../models/reporting/TimesheetEmployeeHoursReport";
import {ViewTitleDisplay} from "./ViewTitleDisplay";
import {TimesheetModule, TimesheetPrivileges} from "../../models/timesheet/Timesheet";
import {getInitialViewSettings} from "../../models/common/ViewSettings";
import {NumbersSideBySide} from "../common/data-display/NumbersSideBySide";
import {TextDisplay} from "../common/data-display/TextDisplay";
import {AuthenticatedUserState} from "../../reducers/common/AuthenticatedUserReducer";
import {makeNumericId} from "../../models/common/Identifier";
import {RouteConfig} from "../../config/RouteConfig";
import {Link} from "react-router-dom";
import {EmployeeAvatar} from "../employees/EmployeeAvatar";

/**
 * typescript
 */
interface MainMenuInjectedProps extends WithStyles< typeof styles >, LanguageProp {
    mainMenu: MainMenuState
    notification: UserNotificationState,
    timesheetReportState: TimesheetReportState,
    actions: {
        fetchMenuTimesheetHoursReport: typeof fetchMenuTimesheetHoursReport
        fetchWorkhoursCurrentWeek: typeof fetchWorkhoursCurrentWeek
        fetchUnreadNotificationsCount: typeof fetchUnreadNotificationsCount
        eventNotificationShow: typeof eventNotificationShow
        fetchUserNotificationUnread: typeof fetchUserNotificationUnread
        markUserNotificationAsRead: typeof markUserNotificationAsRead
        fetchMenuItems: typeof fetchMenuItems,
        userLogout: typeof userLogout,
        helpChangeViewableState: typeof helpChangeViewableState
    }       
}

interface MainMenuOwnProps {
    authUser: AuthenticatedUserState
    viewTitle: string | ReactElement
    onMenuBtnClick: () => void
}
interface MainMenuProps extends MainMenuOwnProps, MainMenuInjectedProps {}

interface MainMenuComponentState {
    anchorEl: Nullable< EventTarget & Element >
    mobileMoreAnchorEl: Nullable< EventTarget & Element >
    isDrawerOpen: boolean
    isNotificationDrawerOpen: boolean
    week: DateRange
    reportKey: string
}

const styles = ( theme: Theme ) => createStyles({
    root: { width: '100%', },
    grow: { flexGrow: 1, },
    notification: { textAlign: "right" },
    nonAuthTitle: { paddingLeft: 15+"px" },
    sectionTitle: {  display: "flex" },
    workhourPerWeek: {

        fontSize: 13,
        textAlign: "center"
    },
    workhoursContainer: {
        border: "1px solid white",
        fontSize: 13,
        height: "2.5em",
        minWidth: 127+"px",
        padding: "3px 3px 6px 3px",
        borderRadius: "5px",
        textAlign: "center"
    },
    sectionDesktop: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
            justifyContent: "flex-end",
        },
    },
    sectionMenuIcons: {
        display: 'flex',
        width: "25%",
        justifyContent: "flex-end",
        marginLeft: "auto",
        alignItems: "center"
    },
    sectionMobile: {
        display: 'flex',
        justifyContent: "flex-end",
        width: "70%",
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
});


/**
 * Represents the main navigation interface and toolbar for the application.
 * Provides user-specific menu items, notification indicators, and access to user-related actions.
 */
class MainMenu extends React.Component< MainMenuProps, MainMenuComponentState> {

    constructor( props: MainMenuProps ) {
        super( props );

        const week = getWeekRange( new Date() );
        this.state = {
            anchorEl: null,
            mobileMoreAnchorEl: null,
            isDrawerOpen: false,
            isNotificationDrawerOpen: false,
            week: week,
            reportKey: createEmployeeHoursReportKey( props.authUser.current?.id ?? makeNumericId(0), week )
        };
    }



    componentDidMount() {

        this.fetchMenuEntries();

    }


    fetchMenuEntries() {


        if(( this.props.authUser.current?.id?.rawValue ?? 0 ) > 0 && this.props.authUser.isLoggedIn ) {


            if (this.props.mainMenu.menu.navItems === null) {
                this.props.actions.fetchMenuItems();
            }
            this.props.actions.fetchWorkhoursCurrentWeek();
            this.props.actions.fetchUnreadNotificationsCount();
            this.props.actions.fetchUserNotificationUnread();

            //check if EventSource for SSE is available, if it is not available poll the backend to check if there is a new notification
            /*if (typeof EventSource !== "undefined") {
                let evtSource = new EventSource(APP_CONFIG.baseWeb + "user/notifications/events/", {withCredentials: true});

                evtSource.addEventListener("notification", (event: Event | MessageEvent) => {
                    // console.log( event );
                    if ('data' in event) {
                        const object = JSON.parse(event.data);
                        this.props.actions.eventNotificationShow(EVENT_NOTIFICATION_TYPE.notification, object.message, 15000);
                        this.props.actions.fetchUnreadNotificationsCount();
                        this.props.actions.fetchUserNotificationUnread();
                    }
                }, false);
            }*/

            //employee hours this week
            const reportKey = createEmployeeHoursReportKey(this.props.authUser.current!!.id, this.state.week);


            if (
                !this.props.timesheetReportState.authUserHoursByWeek.entry.hasOwnProperty(reportKey)
                || this.props.timesheetReportState.authUserHoursByWeek.entry[reportKey] === null
            ) {
                this.props.actions.fetchMenuTimesheetHoursReport(
                    [this.props.authUser.current!!.id],
                    this.state.week,
                    getInitialViewSettings(TimesheetModule.identifier)
                );
            }
        }
    }

    componentDidUpdate(prevProps: Readonly<MainMenuProps>, prevState: Readonly<MainMenuComponentState>, snapshot?: any) {
        if( prevProps.authUser !== this.props.authUser ) {

            this.fetchMenuEntries();
        }

    }

    handleProfileMenuOpen = ( event: React.SyntheticEvent ) => {
        this.setState({ anchorEl: event.currentTarget });
    };

    handleProfileMenuClose = () => {
        this.setState({ anchorEl: null });
        this.handleMobileMenuClose();
    };

    handleMobileMenuClose = () => {
        this.setState({ mobileMoreAnchorEl: null });
    };



    toggleNotificationDrawer = ( isOpen: boolean ) => {
        this.setState({ isNotificationDrawerOpen: isOpen });
    };

    getNotificationIconComponent() {
        const { numberOfUnreadNotifications } = this.props.notification;
        if( numberOfUnreadNotifications > 0 ) {
            return (
                <Badge badgeContent={numberOfUnreadNotifications} color="secondary">
                    <NotificationsIcon />
                </Badge>
            );
        } else {
            return (
                <NotificationsIcon/>
            )
        }
    }

    getEmployeeHours(): Nullable< TimesheetEmployeeHoursReport > {
        const { timesheetReportState } = this.props;
        const reportKey = createEmployeeHoursReportKey(this.props.authUser.current!!.id, this.state.week);

        if(
            ! timesheetReportState.authUserHoursByWeek.entry.hasOwnProperty( reportKey )
            || timesheetReportState.authUserHoursByWeek.entry[ reportKey ] === null
        ) {
            return null;
        } else {
            return timesheetReportState.authUserHoursByWeek.entry[ reportKey ];
        }
    }

    render() {

        const { anchorEl, /*mobileMoreAnchorEl*/ } = this.state;
        const { classes, language, viewTitle, authUser } = this.props;

        if(  ( this.props.authUser.current?.id?.rawValue ?? 0 ) <= 0 ) {
            return (
                <AppBar position="fixed" elevation={0} style={{ height: "64px" }}>
                    <div className={classes.nonAuthTitle}>
                        <ViewTitleDisplay
                            title={viewTitle}
                            language={language}
                        />
                    </div>
                </AppBar>
            );
        }



        const isMenuOpen = Boolean(anchorEl);

        const hours = this.getEmployeeHours();
        const mandatoryHours = hours?.mandatoryHours ?? 0;
        const totalHours = hours?.totalHours ?? 0;

        const renderProfileMenu = (
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={isMenuOpen}
                style={{ marginTop: 30 }}
                onClose={this.handleProfileMenuClose}
            >
                <MenuItem>
                    <Link
                        to={ RouteConfig.userCP.routes.main.path }
                        onClick={ this.handleProfileMenuClose }
                    >
                        <TextDisplay variant="body1">
                            { language.usercp.title }
                        </TextDisplay>

                    </Link>
                </MenuItem>
                <MenuItem>
                    <div onClick={() => this.props.actions.userLogout()}>
                        <TextDisplay variant="body1">
                            {language.userManagment.logout}
                        </TextDisplay>
                    </div>
                </MenuItem>
            </Menu>
        );


        return (
            <div className="App">
                <AppBar
                    position="fixed"
                    elevation={0}
                    style={{ height: 64+"px", marginLeft: "70px" }}
                    className="nav-drawer-header"
                >
                <ToolBar>
                    <div style={{ marginRight: "5px" }}>
                        <IconButton color="inherit" onClick={() => this.props.onMenuBtnClick()} >
                            <MenuIcon />
                        </IconButton>
                    </div>

                        <div className={classes.sectionTitle}>
                            <ViewTitleDisplay title={viewTitle} language={language} />
                        </div>
                        <div className={classes.sectionMenuIcons}>
                            { TimesheetModule.isPrivilegedOneOf(
                                authUser.current,
                                [ TimesheetPrivileges.ACCESS_ALL_HOURS_REPORTS, TimesheetPrivileges.ACCESS_OWN_HOURS_REPORTS ]
                            )
                               ? (
                                   <div className={classes.sectionDesktop}>
                                        <Tooltip title={language.timesheet.hoursCurrentWeekWithArgs(mandatoryHours, totalHours)}>
                                            <div className={ classes.workhoursContainer }>
                                                <div>
                                                    <TextDisplay textColor={ 'common.white' } variant="h6" className={classes.workhourPerWeek}>
                                                        {language.timesheet.hoursCurrentWeek}
                                                    </TextDisplay>
                                                </div>
                                                <div>
                                                    <NumbersSideBySide
                                                        textColor={ 'common.white' }
                                                        variant='body2'
                                                        left={{ value: totalHours } }
                                                        right={{ value: mandatoryHours }}
                                                    />
                                                </div>
                                            </div>
                                        </Tooltip>
                                    </div>
                               )
                               : null
                            }
                            <div>
                                <HelpView />
                            </div>
                            <Tooltip title={ language.notification.unreadNotificationsWithArgs( this.props.notification.numberOfUnreadNotifications) }>
                                <IconButton color="inherit" className={classes.notification} onClick={() => this.toggleNotificationDrawer(true)}>
                                    { this.getNotificationIconComponent() }
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={language.userSettings.mainMenuTooltip}>
                                <IconButton
                                    aria-owns={isMenuOpen ? 'material-appbar' : undefined}
                                    aria-haspopup="true"
                                    onClick={this.handleProfileMenuOpen}
                                    color="inherit"
                                >
                                    {
                                        typeof this.props.authUser.current !== 'undefined' && this.props.authUser.current !== null
                                            ? (
                                                <EmployeeAvatar
                                                    variant="medium"
                                                    employee={ {
                                                        ...this.props.authUser.current,
                                                        userId: this.props.authUser.current.id
                                                    }}
                                                />
                                            )
                                            : <AccountCircle />
                                    }

                                </IconButton>
                            </Tooltip>
                        </div>
                    </ToolBar>
                </AppBar>
                <NotificationDrawer
                    notifications={this.props.notification.unreadNotifications}
                    markNotificationAsRead={ this.props.actions.markUserNotificationAsRead }
                    isOpen={this.state.isNotificationDrawerOpen}
                    onClose={() => this.toggleNotificationDrawer(false) }
                    language={ language }
                />
                {renderProfileMenu}
            </div>
        );
    }
}

//redux state to props function
function mapStateToProps(state: AppState, ownProps: MainMenuOwnProps) {
    return {
        mainMenu: state.MainMenuReducer,
        notification: state.NotificationReducer,
        timesheetReportState: state.TimesheetReportReducer,
        authUser: state.AuthenticatedUserReducer
    }
}

//map redux actions to prop action, to dispatch the actions from the component
function mapDispatchToProps( dispatch: Dispatch ) {
    return {
        actions: bindActionCreators({
            fetchWorkhoursCurrentWeek,
            fetchUnreadNotificationsCount,
            eventNotificationShow: eventNotificationShow,
            fetchUserNotificationUnread,
            markUserNotificationAsRead,
            fetchMenuItems,
            userLogout,
            helpChangeViewableState,
            fetchMenuTimesheetHoursReport,
        }, dispatch )
    }
}

export default compose<React.ComponentClass>(
    connect( mapStateToProps, mapDispatchToProps ),
    withStyles( styles )
)( MainMenu );